import { Text, View } from '@zinc/ui';
import createContext from 'bz-utilities/hooks/context-v2';
import { forwardRef } from 'react';
import FieldSelectItem from './item';

export const [OnLocalChangeCtx, useOnLocalChange] =
  createContext<(e: any) => void>('OnLocalChangeCtx');
export const [ValueMemberCtx, useValueMember] = createContext<string | number>('ValueMemberCtx');
export const [BoldItemKeyCtx, useBoldItemKey] = createContext<string | undefined>('BoldItemKeyCtx');
export const [DisplayMemberCtx, useDisplayMember] = createContext<string | number>(
  'DisplayMemberCtx'
);
export const [SecondaryDisplayMemberCtx, useSecondaryDisplayMember] = createContext<
  string | undefined
>('SecondaryDisplayMemberCtx');
export const [ItemStartSlotCtx, useItemStartSlot] = createContext<
  ((item: any) => React.ReactNode) | undefined
>('ItemStartSlotCtx');

export interface IProps<T> extends React.PropsWithChildren {
  item: T;
  'data-index': number;
  style?: React.CSSProperties;
}

type GenericFRC = <T extends unknown>(
  ...args: Parameters<React.FC<IProps<T>>>
) => ReturnType<React.ForwardRefExoticComponent<IProps<T> & React.RefAttributes<unknown>>>;

const FieldSelectItemDefaultTemplate = forwardRef(
  <T extends { disabled?: boolean }>(props: IProps<T>, ref) => {
    const { item, style, ...other } = props;
    const valueMember = useValueMember() as keyof T;
    const boldItemKey = useBoldItemKey() as keyof T;
    const displayMember = useDisplayMember() as keyof T;
    const secondaryDisplayMember = useSecondaryDisplayMember() as keyof T;
    const itemStartSlot = useItemStartSlot();

    return (
      <FieldSelectItem ref={ref} {...props}>
        <View direction="row" gap={2} align="center">
          {itemStartSlot && <div>{itemStartSlot(item)}</div>}
          <Text
            className="text-ellipsis text-nowrap"
            weight={!!item[boldItemKey] ? 'bold' : 'medium'}
          >
            {(item[displayMember] ?? item[valueMember]) as React.ReactNode}
          </Text>
        </View>
        {secondaryDisplayMember && (
          <Text variant="caption-1" color="neutral-faded" className="mb-[-8px]">
            {item[secondaryDisplayMember] as React.ReactNode}
          </Text>
        )}
      </FieldSelectItem>
    );
  }
) as GenericFRC;

export default FieldSelectItemDefaultTemplate;
